import React, { useEffect, useCallback } from 'react';
import styles from './HerstellerFilter.module.css';
import cx from 'classnames';
import { Hersteller } from '../../../../types/Hersteller';
import { useOnboardingContext } from '../../../../contexts/OnboardingContext';
import { branchCategoryIdsKey } from '../../../../helpers/branchCategoryHelper';

interface HerstellerFilterProps {
    isActive?: boolean;
    branchCategoryIds?: number[];
    categoryId?: number | null;
}

function HerstellerCard({
    h,
    selected,
    onClick,
}: {
    h: Hersteller;
    selected: boolean;
    onClick: () => void;
}) {
    return (
        <div
            role="button"
            tabIndex={0}
            className={cx(styles['hersteller-card'], selected && styles['selected'])}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            {selected && (
                <div className={styles['hersteller-checkmark']}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="12" fill="#D8232A" />
                        <path
                            d="M7 12L10 15L17 8"
                            stroke="white"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            )}
            <div className={styles['hersteller-card-content']}>
                <div className={styles['hersteller-icon']}>
                    {h.imageUrl ? (
                        <img
                            src={h.imageUrl}
                            alt={h.name}
                            loading="lazy"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const placeholder = (e.target as HTMLImageElement).nextElementSibling;
                                if (placeholder) (placeholder as HTMLElement).style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className={styles['hersteller-placeholder']}
                        style={{ display: h.imageUrl ? 'none' : 'flex' }}
                    >
                        {h.name.slice(0, 2)}
                    </div>
                </div>
                <div className={styles['hersteller-name']}>{h.name}</div>
            </div>
        </div>
    );
}

const HerstellerFilter: React.FC<HerstellerFilterProps> = ({
    isActive = false,
    branchCategoryIds = [],
    categoryId = null,
}) => {
    const { formData, herstellers, herstellersLoading, loadHerstellers, toggleSelectedHersteller } =
        useOnboardingContext();
    const branchKey = branchCategoryIdsKey(branchCategoryIds);

    useEffect(() => {
        if (isActive) {
            loadHerstellers(branchCategoryIds, categoryId);
        }
    }, [isActive, categoryId, branchKey, loadHerstellers]);

    const isSelected = useCallback(
        (id: number) => formData.selectedHerstellers.some((x) => x.id === id),
        [formData.selectedHerstellers]
    );

    return (
        <div className={styles['hersteller-section']} hidden={!isActive}>
            <div className={styles['hersteller-container']}>
                {herstellersLoading ? (
                    <div className={styles['hersteller-grid']}>
                        {Array.from({ length: 8 }, (_, i) => (
                            <div
                                key={i}
                                className={cx(styles['hersteller-card'], styles['hersteller-skeleton'])}
                            >
                                <div className={cx(styles['hersteller-icon'], styles['skeleton-block'])} />
                            </div>
                        ))}
                    </div>
                ) : herstellers.length === 0 ? (
                    <p className={styles['no-results']}>Keine Hersteller verfügbar.</p>
                ) : (
                    <div className={styles['hersteller-grid']}>
                        {herstellers.map((h) => (
                            <HerstellerCard
                                key={h.id}
                                h={h}
                                selected={isSelected(h.id)}
                                onClick={() => toggleSelectedHersteller(h)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HerstellerFilter;
